import { getShiftLabel } from '@/shared/legacy/contexts/DialogContext/modals/EnrichVacancyModal/utils/EnrichVacancyModal.constants';
import { getDictionary, getLanguage } from '@/shared/lib/i18n';

import { formatEuropeanEuroSalaryRange } from './format-european-euro-amount';

type ImprovedJobForWhatsapp = {
  job_details?: {
    job_title?: string;
    salary_range?: string;
    salary_unit?: string;
  } | null;
  employment_requirements?: {
    address?: string;
    city?: string | null;
    shifts?: string[] | string | null;
  } | null;
};

type JobSummaryFallback = {
  jobTitle?: string | null;
};

const capitalizeFirstLetter = (value: string): string =>
  value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const withJobDetailEmoji = (emoji: string, value: string): string => (value === '' ? '' : `${emoji} ${value}`);

const resolveSalaryUnitLabel = (salaryUnit: string | undefined): string => {
  const dictionary = getDictionary();
  const unitKey = salaryUnit?.trim().toLowerCase();

  if (unitKey === 'month' || unitKey === 'monthly') return dictionary.monthly;
  if (unitKey === 'hour' || unitKey === 'hourly') return dictionary.hourly;
  if (unitKey === 'week' || unitKey === 'weekly') return dictionary.weekly;
  if (unitKey === 'year' || unitKey === 'yearly') return dictionary.yearly;

  return salaryUnit?.trim() ?? '';
};

const formatShifts = (shifts: string[] | string | null | undefined): string => {
  if (shifts == null) return '';

  const language = getLanguage();
  const shiftValues = Array.isArray(shifts) ? shifts : shifts.split(',').map((shift) => shift.trim());

  return shiftValues
    .filter(Boolean)
    .map((shift) => getShiftLabel(shift, language))
    .join(', ');
};

export const buildRjWhatsappJobDetails = (
  improvedJob: ImprovedJobForWhatsapp | null,
  fallbackJob?: JobSummaryFallback | null,
) => {
  const rawSalaryRange = improvedJob?.job_details?.salary_range?.trim() ?? '';
  const salaryUnitLabel = resolveSalaryUnitLabel(improvedJob?.job_details?.salary_unit);
  const formattedSalaryRange = rawSalaryRange === '' ? '' : formatEuropeanEuroSalaryRange(rawSalaryRange);
  const vacancySalary =
    formattedSalaryRange === ''
      ? ''
      : salaryUnitLabel
        ? `${formattedSalaryRange} ${salaryUnitLabel}`
        : formattedSalaryRange;

  const rawLocation =
    improvedJob?.employment_requirements?.city?.trim() || improvedJob?.employment_requirements?.address?.trim() || '';

  const vacancyName = improvedJob?.job_details?.job_title ?? fallbackJob?.jobTitle ?? '';

  return {
    vacancyName: withJobDetailEmoji('💼', vacancyName),
    vacancyLocation: withJobDetailEmoji('📌', capitalizeFirstLetter(rawLocation)),
    vacancySalary: withJobDetailEmoji('💰', vacancySalary),
    vacancyShift: withJobDetailEmoji('🕒', formatShifts(improvedJob?.employment_requirements?.shifts)),
  };
};
